/**
 * CommunityAppBar — top bar for the community wall: a back arrow, a centered
 * tappable title, and a trailing share action.
 *
 * The bar itself is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`); this component
 * stays as the community-owned wrapper because it holds the page's prop
 * contract (`title` / `onBack` / `onShare`) and builds the share-action node.
 * It is the one migrated surface that uses **all three** slots — leading,
 * tappable title and trailing `actions`.
 *
 * Flutter source of truth: `appBar` / `appBarTitle` / `appBarLeading`
 * (`community_screen.dart:1043-1073`):
 *  - leading back arrow → `onBackPress` (`community_screen.dart:1110-1117`).
 *  - centered title: `"My Community"` for the owner, else the apostrophe-`s`
 *    possessive `"<firstName>'s Community"` / `"<firstName>' Community"`
 *    (`community_screen.dart:1075-1108`, strings at `:1077-1085`). Tapping the
 *    title also goes back (`community_screen.dart:1086-1090`).
 *  - trailing share action (`share_social.png`) → `_onShareSocialUser`
 *    (`community_screen.dart:1051-1070`).
 *
 * The title string (incl. the apostrophe-`s` rule) is resolved by the host page
 * and passed in pre-rendered, so this component stays presentation-only.
 *
 * `sticky={false}` is mandatory here: `CommunityPage` already owns the
 * `position: sticky; top: 0; zIndex: 50` wrapper around this bar (it is the
 * measured `topInsetRef` element), and a nested second sticky context would
 * break the measured top inset the messages list depends on. That wrapper no
 * longer applies `paddingTop: responsive.safeArea.top` — `ExpauseAppBar` folds
 * the top inset into its own `calc(topBar.height + safeArea.top)` box, so the
 * measured height is unchanged and the inset is applied exactly once.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` changes the title from
 * `responsive.font.lg` to `responsive.font.xl` (still `bold`) and adds the
 * shared bar's translucent blurred backdrop — which reads as the previous flat
 * fill here, since `CommunityPage`'s sticky wrapper stays opaque
 * (`backgroundColor: theme.background`). `titleQa` carries the page's existing
 * `community-title` id, and `rootQa` the page-unique `community-app-bar` id —
 * the shared bar mints no `data-qa-*` of its own (see `ExpauseAppBar`'s
 * `rootQa` doc for why a bare default id is unsafe under route caching).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface CommunityAppBarProps {
  /** Pre-rendered, localized title (owner vs apostrophe-`s` possessive). */
  title: string;
  /** Tap target for the back arrow (and the title — Flutter parity). */
  onBack: () => void;
  /** Tap target for the share action (the web `_onShareSocialUser`). */
  onShare: () => void;
}

export const CommunityAppBar: React.FC<CommunityAppBarProps> = ({
  title,
  onBack,
  onShare,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <ExpauseAppBar
      // Page-unique bar-root handle. Load-bearing here beyond the usual QA
      // convenience: with `sticky={false}` a test cannot reach this bar by
      // walking up to the nearest sticky ancestor — that walk lands on
      // `CommunityPage`'s own sticky wrapper instead.
      rootQa={{ id: 'community-app-bar' }}
      // `CommunityPage` owns the sticky wrapper (the measured top inset).
      sticky={false}
      // Centered title (`appBarTitle`, `community_screen.dart:1075-1108`), also
      // a back affordance matching Flutter (`community_screen.dart:1086-1090`).
      title={title}
      onTitleTap={onBack}
      titleQa={{ id: 'community-title' }}
      // Leading: back arrow (`appBarLeading`, `community_screen.dart:1110-1117`).
      onLeadingTap={onBack}
      leadingQa={{ id: 'community-back' }}
      // Trailing: share action (`community_screen.dart:1051-1070`).
      actions={
        <button
          {...qaAttr({ id: 'community-share-button' })}
          onClick={onShare}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0,
          }}
        >
          <AssetIcon
            path="/assets/images/share_social.png"
            width={responsive.icon.large}
            height={responsive.icon.large}
            color={theme.whiteLight}
          />
        </button>
      }
    />
  );
};
